// ============================================
// JourneyStepConfig
// Admin panel for configuring journey step display names.
// Persists to Firestore via journeyConfigService.
// No framer-motion — pure CSS transitions.
// ============================================

import { useState, useEffect } from 'react';
import { Save, RotateCcw, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { JOURNEY_STEP_KEYS, DEFAULT_JOURNEY_CONFIG } from '../../../config/journeyConfig';
import type { JourneyStepLabels } from '../../../config/journeyConfig';
import {
  getSavedStepLabels,
  saveJourneyStepLabels,
  resetJourneyStepLabels,
} from '../../../services/firestore/journeyConfigService';

type Status = 'idle' | 'loading' | 'saving' | 'saved' | 'error';

export function JourneyStepConfig() {
  const [labels, setLabels] = useState<JourneyStepLabels>({ ...DEFAULT_JOURNEY_CONFIG.stepLabels });
  const [status, setStatus] = useState<Status>('loading');
  const [error, setError] = useState<string | null>(null);

  // Load saved labels on mount
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const saved = await getSavedStepLabels();
        if (!cancelled) {
          setLabels(saved);
          setStatus('idle');
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load');
          setStatus('error');
        }
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  function handleChange(key: string, value: string) {
    setLabels((prev) => ({ ...prev, [key]: value }));
    if (status === 'saved') setStatus('idle');
  }

  async function handleSave() {
    setStatus('saving');
    setError(null);
    try {
      await saveJourneyStepLabels(labels, 'admin');
      setStatus('saved');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
      setStatus('error');
    }
  }

  async function handleReset() {
    setStatus('saving');
    setError(null);
    try {
      await resetJourneyStepLabels('admin');
      setLabels({ ...DEFAULT_JOURNEY_CONFIG.stepLabels });
      setStatus('saved');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Reset failed');
      setStatus('error');
    }
  }

  const hasChanges = JOURNEY_STEP_KEYS.some(
    (key) => labels[key] !== DEFAULT_JOURNEY_CONFIG.stepLabels[key],
  );

  if (status === 'loading') {
    return (
      <div className="flex items-center gap-2 text-text-secondary py-8 justify-center">
        <Loader2 size={16} className="animate-spin" />
        <span>Loading step configuration...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-text-primary mb-1">Journey Step Names</h3>
        <p className="text-sm text-text-secondary">
          Customize the display names for each journey step. These labels appear in the patient
          dashboard, admin views, and journey tracking.
        </p>
      </div>

      {/* Step label grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {JOURNEY_STEP_KEYS.map((key) => {
          const isModified = labels[key] !== DEFAULT_JOURNEY_CONFIG.stepLabels[key];
          return (
            <div key={key} className="space-y-1">
              <label className="flex items-center gap-2 text-sm font-medium text-text-secondary">
                <code className="text-xs font-mono bg-bg-tertiary px-1.5 py-0.5 rounded">
                  {key}
                </code>
                {isModified && (
                  <span className="text-xs text-[#D97706]">modified</span>
                )}
              </label>
              <input
                type="text"
                value={labels[key]}
                onChange={(e) => handleChange(key, e.target.value)}
                placeholder={DEFAULT_JOURNEY_CONFIG.stepLabels[key]}
                className="w-full px-3 py-2 text-sm text-text-primary bg-bg-card border border-bg-tertiary rounded focus:outline-none focus:ring-1 focus:ring-gold"
                style={{ minHeight: '38px' }}
              />
              {isModified && (
                <span className="text-xs text-text-muted">
                  Default: {DEFAULT_JOURNEY_CONFIG.stepLabels[key]}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-3 pt-2">
        <button
          onClick={handleSave}
          disabled={status === 'saving'}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gold rounded hover:bg-gold/90 transition-colors disabled:opacity-50"
        >
          {status === 'saving' ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <Save size={14} />
          )}
          Save Labels
        </button>

        <button
          onClick={handleReset}
          disabled={status === 'saving' || !hasChanges}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-text-secondary bg-bg-tertiary rounded hover:bg-bg-tertiary/80 transition-colors disabled:opacity-50"
        >
          <RotateCcw size={14} />
          Reset to Defaults
        </button>

        {/* Status indicator */}
        {status === 'saved' && (
          <span className="flex items-center gap-1 text-sm text-[#059669]">
            <CheckCircle2 size={14} />
            Saved
          </span>
        )}
        {status === 'error' && error && (
          <span className="flex items-center gap-1 text-sm text-red-500">
            <AlertCircle size={14} />
            {error}
          </span>
        )}
      </div>

      {/* Info */}
      <div className="text-xs text-text-muted border-t border-bg-tertiary pt-4">
        Changes take effect immediately for all users. Step keys (shown in monospace)
        are immutable — only the display labels are configurable.
      </div>
    </div>
  );
}
